/**
 * Module-level session store. **In memory only** — the access token and the CSRF token
 * are never written to localStorage, sessionStorage or a JS-set cookie (repo guardrail;
 * genesis handoff §5). The refresh token is an HttpOnly cookie the SPA never sees.
 *
 * There is no JWT decoding here: Brand Center's tenant/roles come from
 * `GET /api/auth/me`, never from client-side claim parsing.
 */

interface StoreState {
  accessToken: string | null;
  /** Epoch ms, or null when the response carried no expiry. */
  expiresAt: number | null;
  csrfToken: string | null;
}

const state: StoreState = {
  accessToken: null,
  expiresAt: null,
  csrfToken: null,
};

/** Notified after `signOut()` so React state (AuthContext) follows the store. */
let signOutCallback: (() => void) | null = null;
/** Notified when a silent refresh fails — the app restarts the login kickoff. */
let sessionExpiredHandler: (() => void) | null = null;

export function getAccessToken(): string | null {
  return state.accessToken;
}

export function getExpiresAt(): number | null {
  return state.expiresAt;
}

export function setAccessToken(token: string | null, expiresAt: number | null = null): void {
  state.accessToken = token;
  state.expiresAt = expiresAt;
}

export function getCsrfToken(): string | null {
  return state.csrfToken;
}

/**
 * Store-token CSRF value, captured from the login/refresh/exchange response body and
 * sent as `X-CSRF-Token` on writes. Cleared by `signOut()`.
 */
export function setCsrfToken(token: string | null): void {
  state.csrfToken = token;
}

export function registerSignOutCallback(cb: (() => void) | null): void {
  signOutCallback = cb;
}

export function registerSessionExpiredHandler(cb: (() => void) | null): void {
  sessionExpiredHandler = cb;
}

export function notifySessionExpired(): void {
  sessionExpiredHandler?.();
}

/** Drop the whole in-memory session. */
export function signOut(): void {
  state.accessToken = null;
  state.expiresAt = null;
  state.csrfToken = null;
  signOutCallback?.();
}

/** Test seam — clears tokens AND both callback registrations. */
export function __resetAuthStore(): void {
  state.accessToken = null;
  state.expiresAt = null;
  state.csrfToken = null;
  signOutCallback = null;
  sessionExpiredHandler = null;
}
